import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { useBrands } from "../../contexts/BrandContext";
import { toast } from "sonner";
import {
  PlayCircle, Clock, Building2, MapPin, RefreshCw, LogOut, Loader2,
  Search, Inbox, Pencil, Trash2,
} from "lucide-react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { formatDateFr } from "../../lib/dateFormat";
import ManualClockDialog from "../ManualClockDialog";

/**
 * Onglet "En cours d'exécution" — liste des pointages ouverts (entrée
 * enregistrée, sortie absente).
 *
 * Présentation calquée sur EquipmentRequestsTab.jsx : barre colorée latérale,
 * badges majuscules, liste paginée par divider, recherche dédiée.
 */
export default function PointageEnCoursTab({
  brands = [],
  users = [],
  sites = [],
  isManager = false,
  isAdmin = false,
  onEdit,
  onDelete,
  refreshSignal = 0,
}) {
  const { selectedBrandId } = useBrands();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [manualClock, setManualClock] = useState({ open: false, target: null });

  const load = useCallback(async () => {
    try {
      setRefreshing(true);
      const params = selectedBrandId ? { brand_id: selectedBrandId } : {};
      const { data } = await api.get("/timeentries", { params });
      setList(data || []);
    } catch {
      setList([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [selectedBrandId]);

  useEffect(() => { load(); }, [load, refreshSignal]);

  const openEntries = useMemo(
    () => list.filter((t) => t.clock_in && !t.clock_out),
    [list],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return openEntries;
    return openEntries.filter((t) => {
      const u = users.find((x) => x.id === t.collaborateur_id);
      const b = brands.find((x) => x.id === t.brand_id);
      const s = sites.find((x) => x.id === (t.site_id || t.nearest_site_id));
      return (
        (u?.name || "").toLowerCase().includes(q)
        || (u?.email || "").toLowerCase().includes(q)
        || (b?.name || "").toLowerCase().includes(q)
        || (s?.name || "").toLowerCase().includes(q)
        || (t.address || "").toLowerCase().includes(q)
      );
    });
  }, [openEntries, search, users, brands, sites]);

  const elapsedFrom = (clockIn, date) => {
    if (!clockIn || !date) return null;
    try {
      const [h, m] = clockIn.split(":").map(Number);
      const start = new Date(`${date}T00:00:00`);
      start.setHours(h, m, 0, 0);
      const diffMin = Math.max(0, Math.round((Date.now() - start.getTime()) / 60000));
      if (diffMin < 60) return `${diffMin} min`;
      const hh = Math.floor(diffMin / 60);
      const mm = diffMin % 60;
      return `${hh}h${String(mm).padStart(2, "0")}`;
    } catch { return null; }
  };

  const openManualClockOut = async (t) => {
    const u = users.find((x) => x.id === t.collaborateur_id);
    const name = u?.name || "ce collaborateur";
    let plannedEnd = null;
    if (t.shift_id) {
      try {
        const r = await api.get(`/shifts/${t.shift_id}`);
        plannedEnd = r.data?.end_time || null;
      } catch { /* fallback */ }
    }
    if (!plannedEnd) {
      const d = new Date();
      plannedEnd = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    }
    setManualClock({
      open: true,
      target: {
        entry_id: t.id,
        collaborateur_name: name,
        clock_in: t.clock_in,
        planned_end: plannedEnd,
      },
    });
  };

  return (
    <div className="space-y-4" data-testid="pointage-encours-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="label-uppercase mb-1 inline-flex items-center gap-1.5">
            <PlayCircle size={11} /> Suivi temps réel
          </p>
          <h2 className="font-display font-semibold text-xl text-zinc-900">
            Pointages en cours d'exécution
          </h2>
          <p className="text-xs text-zinc-600 mt-1 font-mono">
            Collaborateurs actuellement pointés (entrée sans sortie) · {openEntries.length} en cours
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={load}
          disabled={refreshing}
          data-testid="pointage-encours-refresh"
        >
          <RefreshCw size={13} className={`mr-1.5 ${refreshing ? "animate-spin" : ""}`} />
          Rafraîchir
        </Button>
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm p-3">
        <Label className="label-uppercase mb-1.5 block inline-flex items-center gap-1.5">
          <Search size={10} /> Recherche
        </Label>
        <Input
          placeholder="Collaborateur, département, site ou adresse…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="pointage-encours-search"
        />
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm">
        {loading && filtered.length === 0 ? (
          <p className="text-sm text-zinc-500 font-mono py-10 text-center">Chargement…</p>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center" data-testid="pointage-encours-empty">
            <Inbox className="mx-auto text-zinc-300 mb-3" size={36} />
            <p className="text-sm text-zinc-500 font-mono">
              {openEntries.length === 0
                ? "Aucun pointage en cours pour le moment."
                : "Aucun pointage ne correspond à votre recherche."}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-zinc-100">
            {filtered.map((t) => {
              const u = users.find((x) => x.id === t.collaborateur_id);
              const brand = brands.find((b) => b.id === t.brand_id);
              const site = sites.find((s) => s.id === (t.site_id || t.nearest_site_id));
              const elapsed = elapsedFrom(t.clock_in, t.date);
              return (
                <div
                  key={t.id}
                  className="p-4 flex items-start gap-3 flex-wrap"
                  data-testid={`pointage-encours-row-${t.id}`}
                >
                  <span
                    className="h-12 w-1 rounded-sm flex-shrink-0"
                    style={{ backgroundColor: brand?.color || "#2563EB" }}
                  />
                  <div className="flex-1 min-w-[240px]">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase font-semibold px-2 py-0.5 border rounded-sm tracking-wider bg-blue-50 border-blue-200 text-blue-800">
                        <PlayCircle size={10} /> En cours
                      </span>
                      {u?.id ? (
                        <Link
                          to={`/collaborateurs/${u.id}/dossier`}
                          className="text-sm font-medium text-zinc-900 hover:text-blue-700 hover:underline underline-offset-2"
                          data-testid={`pointage-encours-collab-${t.id}`}
                        >
                          {u.name}
                        </Link>
                      ) : (
                        <p className="text-sm font-medium text-zinc-900">—</p>
                      )}
                      {elapsed && (
                        <span
                          className="text-[10px] uppercase font-semibold px-2 py-0.5 border rounded-sm tracking-wider bg-blue-50 border-blue-200 text-blue-800"
                          data-testid={`pointage-encours-elapsed-${t.id}`}
                        >
                          {elapsed} écoulées
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-0.5 mt-1 text-[11px] font-mono text-zinc-600">
                      <p className="inline-flex items-center gap-1.5">
                        <Clock size={11} className="text-zinc-400" />
                        Entrée à <b className="text-zinc-800">{t.clock_in}</b>
                        {t.date && <span className="text-zinc-400">· {formatDateFr(t.date)}</span>}
                      </p>
                      <p className="inline-flex items-center gap-1.5">
                        <Building2 size={11} className="text-zinc-400" /> {brand?.name || "—"}
                      </p>
                      {site?.name && (
                        <p className="inline-flex items-center gap-1.5">
                          <MapPin size={11} className="text-zinc-400" /> {site.name}
                        </p>
                      )}
                    </div>
                    {t.address && (
                      <p className="text-[11px] text-zinc-500 font-mono mt-1 inline-flex items-center gap-1.5">
                        <MapPin size={10} className="text-zinc-400" />
                        <span className="truncate-2">{t.address}</span>
                      </p>
                    )}
                  </div>
                  {isManager && (
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => openManualClockOut(t)}
                        disabled={busyId === t.id}
                        className="border-zinc-200 text-zinc-800 hover:bg-zinc-50"
                        data-testid={`pointage-encours-manual-clock-out-${t.id}`}
                      >
                        {busyId === t.id
                          ? <Loader2 size={13} className="mr-1.5 animate-spin" />
                          : <LogOut size={13} className="mr-1.5" />}
                        Pointer sortie
                      </Button>
                      {isAdmin && (
                        <>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => onEdit?.(t)}
                            className="h-8 px-2 text-zinc-700 border-zinc-200 hover:bg-zinc-50"
                            data-testid={`pointage-encours-edit-${t.id}`}
                            title="Modifier"
                          >
                            <Pencil size={13} />
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={async () => {
                              setBusyId(t.id);
                              try { await onDelete?.(t); await load(); }
                              finally { setBusyId(null); }
                            }}
                            disabled={busyId === t.id}
                            className="h-8 px-2 text-red-700 border-red-200 hover:bg-red-50"
                            data-testid={`pointage-encours-delete-${t.id}`}
                            title="Supprimer"
                          >
                            <Trash2 size={13} />
                          </Button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <ManualClockDialog
        open={manualClock.open}
        onOpenChange={(v) => setManualClock((s) => ({ ...s, open: v }))}
        mode="out"
        target={manualClock.target}
        onSuccess={() => load()}
      />
    </div>
  );
}
